import { getCustomerPlatforms, updateWhatsappGeneralInfo } from "@/api/customer-platform";
import { metaIntegrationService } from "@/api/customer-platform/whatsapp/meta-integration";
import { checkGupshupAppStatus } from "@/api/customer-platform/whatsapp/gupshup/check-app-status";
import { configureGupshupSubscription } from "@/api/customer-platform/whatsapp/gupshup/configure-subscription";
import { createGupshupOnboarding } from "@/api/customer-platform/whatsapp/gupshup/create-onboarding";
import { unsubscribeGupshup } from "@/api/customer-platform/whatsapp/gupshup/unsubscribe";
import { updateGupshupGeneralInfo } from "@/api/customer-platform/whatsapp/gupshup/update-general-info";
import {
  ICustomerPlatform,
  ICustomerPlatformInput,
  IGupshupAppDetails,
  IGupshupOnboardingRequest,
  IGupshupOnboardingResponse,
  IWhatsappUpdateGeneralInfoInput,
} from "@/interfaces/customer-platform.interfaces";
import { apiT2B } from "@/lib/axios";

class CustomerPlatformService {
  async whatsappSubscription(
    data: ICustomerPlatformInput,
    token: string,
    customerId: string
  ): Promise<void> {
    const wabaId = data.customerExternalId;
    const phoneNumberId = data.platformProfileId;
    const pin = data.recoveryCode;

    if (!wabaId || !phoneNumberId || !pin) {
      throw new Error("Missing required WhatsApp Business Account ID, Phone Number ID, or PIN");
    }

    try {
      
      await metaIntegrationService.subscribeApp(wabaId);

      await metaIntegrationService.registerPhoneNumber(phoneNumberId, pin);

      const phoneData = await metaIntegrationService.getPhoneNumbers(wabaId);

      // A foto de perfil nao deve bloquear a ativacao do numero.
      let profilePictureUrl: string | null = null;
      try {
        profilePictureUrl = await metaIntegrationService.getProfilePictureUrl(phoneNumberId);
      } catch {}

      let accountImage = "";
      if (profilePictureUrl) {
        try {
          const mediaSaveResponse = await apiT2B.post(
            "api/v1/midia/savefromurl",
            { url: profilePictureUrl },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                CustomerId: customerId,
              },
            }
          );
          accountImage = mediaSaveResponse.data?.publicUrl || "";
        } catch (error) {
          
        }
      }

      const { Active, ...restData } = data; 
      const enrichedData = {
        ...restData,
        Active: Active, 
        displayName: phoneData.verified_name || data.displayName || "",
        displayNumber: phoneData.display_phone_number || data.displayNumber || "",
        accountImage: accountImage || data.accountImage || "",
        PlatformAccountInfo: wabaId, 
      };

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          CustomerId: customerId,
        },
      };

      await apiT2B.post("api/v1/customerplatform", enrichedData, config);
    } catch (error) {
      
      try {
        await metaIntegrationService.deregisterPhoneNumber(phoneNumberId);
        await metaIntegrationService.unsubscribeApp(wabaId);
      } catch (rollbackError) {
        
      }
      throw error;
    }
  }

  async whatsappUnsubscribe(
    token: string,
    customerId: string,
    customerPlatformId: number,
    wabaId: number,
    phoneNumberId: number
  ): Promise<void> {
    try {
      await metaIntegrationService.deregisterPhoneNumber(phoneNumberId.toString());
    } catch (error) {
      console.warn("Falha ao desregistrar número na Meta:", error);
    }

    try {
      await metaIntegrationService.unsubscribeApp(wabaId.toString());
    } catch (error) {
      console.warn("Falha ao cancelar inscrição do app na Meta:", error);
    }

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        CustomerId: customerId,
      },
    };

    await apiT2B.delete(`api/v1/customerplatform/${customerPlatformId}`, config);
  }
  async getAll(): Promise<ICustomerPlatform[]> {
    try {
      
      return await getCustomerPlatforms();
    } catch (error) {
      
      return [];
    }
  }

  async whatsappUpdateGeneralInfo({
    whatsPlatform,
  }: IWhatsappUpdateGeneralInfoInput): Promise<ICustomerPlatform> {
    if (whatsPlatform.platformId === 5) {
      return await updateGupshupGeneralInfo(whatsPlatform);
    }
    return await updateWhatsappGeneralInfo(whatsPlatform);
  }

  async gupshupOnboarding(
    request: IGupshupOnboardingRequest
  ): Promise<IGupshupOnboardingResponse> {
    return await createGupshupOnboarding(request);
  }

  async gupshupCheckStatus(appId: string): Promise<IGupshupAppDetails> {
    return await checkGupshupAppStatus(appId);
  }

  async gupshupConfigureSubscription(
    appId: string,
    callbackUrl: string,
    appDetails: {
      phoneNumber: string;
      appName: string;
    },
    platformId: number
  ): Promise<void> {
    return await configureGupshupSubscription(appId, callbackUrl, appDetails, platformId);
  }

  async gupshupUnsubscribe(appId: string, customerPlatformId: number): Promise<void> {
    return await unsubscribeGupshup(appId, customerPlatformId);
  }
}

const customerPlatformService = new CustomerPlatformService();
export default customerPlatformService;
